import type { readCryptoAccountFragment$key } from '@onramp/data/__generated__/readCryptoAccountFragment.graphql';
import type { SourceOfFundsData } from '@onramp/state/types/SourceOfFundsData';
import { isCryptoAccountAllowed } from '@onramp/utils/getIsCountryAllowed';
import { graphql, readInlineData } from '@cbhq/data-layer';

import type { RemainingAvailableLimit } from './getTransferSettingsLimitOrAccountBalance';
import { getTransferSettingsLimitOrAccountBalance } from './getTransferSettingsLimitOrAccountBalance';
import { readAccountBalance } from './readAccountBalance';
import { readAccountBalanceFiat } from './readAccountBalanceFiat';
import type { SourceOfFundsToCommonFormatRequiredData } from './useSourceOfFundsToCommonFormatRequiredData';

const accountFragment = graphql`
  fragment readCryptoAccountFragment on Account @inline {
    ...getIsCountryAllowedAccountFragment
    ...readAccountBalanceFragment
    ...readAccountBalanceFiatFragment
    uuid
    name
    assetOrFiatCurrency {
      __typename
      ... on ViewerAsset {
        asset {
          uuid
          symbol
          name
          imageUrl
        }
      }
      ... on TiersCurrency {
        name
      }
    }
  }
`;

export function readCryptoAccount({
  countryCode,
  inputAccountRef,
  nativeAssetUuid,
  cryptoAccountFiatLimit,
}: {
  inputAccountRef: readCryptoAccountFragment$key;
  nativeAssetUuid: string;
  /** Optional remaining available limit - if provided, uses the minimum of this and the balance */
  cryptoAccountFiatLimit?: RemainingAvailableLimit;
} & SourceOfFundsToCommonFormatRequiredData): SourceOfFundsData | null {
  const inputAccount = readInlineData(accountFragment, inputAccountRef);
  const cryptoBalance = readAccountBalance(inputAccount);
  const fiatBalance = readAccountBalanceFiat(inputAccount);

  if (
    !isCryptoAccountAllowed(countryCode, inputAccount) ||
    inputAccount.assetOrFiatCurrency?.__typename !== 'ViewerAsset'
  ) {
    return null;
  }

  const asset = inputAccount.assetOrFiatCurrency?.asset;
  if (!asset || (Number(cryptoBalance?.value) <= 0 && asset.uuid !== nativeAssetUuid)) {
    return null;
  }

  // crypto sends may be limited by transfer settings limit set on the user's retail account
  // we check the crypto balance (in fiat) against the transfer settings limit (if it exists)
  const { fiatLimit, hasTransferSettingsLimit } = getTransferSettingsLimitOrAccountBalance(
    cryptoAccountFiatLimit,
    fiatBalance,
  );

  return {
    type: 'crypto',
    uuid: inputAccount.uuid,
    assetUuid: asset.uuid,
    title: asset.name,
    meta: asset.symbol,
    description: '',
    iconData: {
      iconUrl: asset.imageUrl,
    },
    limitData: {
      // if transfer settings are enabled, the fiat limit and crypto balance may differ
      // when transfer settings are enabled, use fiat limit for validation
      fiat: fiatLimit,
      crypto: cryptoBalance,
    },
    hasTransferSettingsLimit,
  };
}
